import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { displayProcessor } from '../lib/processorDisplay';
import { Plug, Plus, ShieldCheck, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import useAuthStore from '../lib/auth-store';
import { apiFetch, roleLabel } from '../components/merchant/attributionApi';

const ROW_LABELS = { authentication: 'Authentication', application_api: 'Application API', status_api: 'Status API', reporting_api: 'Reporting API',
  residual_api: 'Residual API', webhooks: 'Webhooks', documents: 'Documents', status_mapping: 'Status mapping' };
const TONE = { READY: 'text-emerald-600', FAILED: 'text-red-600', 'NOT TESTED': 'text-amber-600', 'NOT CONFIGURED': 'text-muted-foreground' };

function Pick({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ConnectionCard({ c, token, reload }) {
  const act = async (path, msg) => { try { await apiFetch(token, `/boarding/providers/connections/${c.id}/${path}`, 'POST'); toast.success(msg); reload(); } catch (e) { toast.error(e.message); } };
  return (
    <Card className="p-5 space-y-3" data-testid={`connection-${c.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{c.label}</p>
          <p className="text-xs text-muted-foreground">{displayProcessor(c.provider_name)} · {roleLabel(c.connection_method)} · credentials {c.credential_reference || 'none'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={c.environment === 'production' ? 'destructive' : 'secondary'}>{c.environment}</Badge>
          <Badge variant="outline" className="capitalize" data-testid={`connection-status-${c.id}`}>{c.status}</Badge>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`connection-readiness-${c.id}`}>
        {Object.entries(c.readiness.rows).map(([k, v]) => (
          <div key={k} className="text-xs"><p className="text-muted-foreground">{ROW_LABELS[k]}</p><p className={`font-semibold ${TONE[v]}`}>{v}</p></div>
        ))}
      </div>
      {c.connection_health?.error && <p className="text-xs text-red-600 break-all">{c.connection_health.error}</p>}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => act('test', 'Connection tested')} data-testid={`connection-test-${c.id}`}><Zap className="w-3.5 h-3.5 mr-1" /> Test connection</Button>
        {c.status === 'active' && <Button size="sm" variant="ghost" onClick={() => act('deactivate', 'Connection deactivated')} data-testid={`connection-deactivate-${c.id}`}>Deactivate</Button>}
        {c.status !== 'active' && <Button size="sm" disabled={!c.readiness.can_activate} onClick={() => act('activate', 'Connection activated')} data-testid={`connection-activate-${c.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Activate</Button>}
      </div>
      {!c.readiness.can_activate && c.status !== 'active' && <p className="text-xs text-muted-foreground">To activate: {c.readiness.blockers.join('; ')}</p>}
    </Card>
  );
}

function ConnectWizard({ catalog, token, onClose, onDone }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ label: '', provider_slug: '', custom_name: '', connection_method: '', environment: 'sandbox', managed: '', creds: {}, base_url: '', status_map: '' });
  const provider = catalog.providers.find((p) => p.slug === f.provider_slug);
  const reqs = provider ? (provider.credential_requirements || []) : [{ key: 'access_token', label: 'API token', secret: true }];
  const managedOpts = catalog.managed_credentials.filter((m) => m.provider_slug === f.provider_slug && (!m.environment || m.environment === f.environment));
  const setCred = (k, v) => setF({ ...f, managed: reqs.find((r) => r.key === k)?.config ? f.managed : '', creds: { ...f.creds, [k]: v } });
  const save = async () => {
    try {
      let slug = f.provider_slug;
      if (slug === '__custom') {
        slug = f.custom_slug || (await apiFetch(token, '/boarding/providers/custom', 'POST', { name: f.custom_name, capabilities: ['application_submission', 'status_lookup'] })).slug;
        setF((prev) => ({ ...prev, custom_slug: slug }));
      }
      const status_mapping = Object.fromEntries(f.status_map.split('\n').map((l) => l.split('=').map((x) => x.trim())).filter((p) => p.length === 2 && p[0]));
      const pick = (cfg) => Object.fromEntries(reqs.filter((r) => !!r.config === cfg && f.creds[r.key]).map((r) => [r.key, f.creds[r.key]]));
      const credentials = f.managed ? {} : pick(false);
      const platform_config = pick(true);
      await apiFetch(token, '/boarding/providers/connections', 'POST', {
        provider_slug: slug, label: f.label || undefined, connection_method: f.connection_method || undefined, environment: f.environment, base_url: f.base_url || undefined,
        managed_credential_env: f.managed || undefined, credentials: Object.keys(credentials).length ? credentials : undefined,
        platform_config: Object.keys(platform_config).length ? platform_config : undefined,
        status_mapping: Object.keys(status_mapping).length ? status_mapping : undefined,
      });
      toast.success('Connection saved — test it, then activate'); onDone();
    } catch (e) { toast.error(e.message); }
  };
  const steps = ['Provider', 'Connection', 'Credentials', 'Capabilities & status mapping'];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="connect-wizard">
        <DialogHeader><DialogTitle>Connect your processor</DialogTitle><DialogDescription>Step {step + 1} of {steps.length}: {steps[step]}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {step === 0 && <>
            <Pick value={f.provider_slug} onChange={(v) => setF({ ...f, provider_slug: v })} placeholder="Choose provider" testId="wizard-provider-select"
                  options={[...catalog.providers.map((p) => [p.slug, `${p.name}${p.adapter_status ? ` (${roleLabel(p.adapter_status)})` : ''}`]), ['__custom', 'Custom provider…']]} />
            {f.provider_slug === '__custom' && <Input placeholder="Provider name" value={f.custom_name} onChange={(e) => setF({ ...f, custom_name: e.target.value, custom_slug: '' })} data-testid="wizard-custom-name" />}
          </>}
          {step === 1 && <>
            <Pick value={f.connection_method} onChange={(v) => setF({ ...f, connection_method: v })} placeholder="Connection method" testId="wizard-method"
                  options={(provider?.connection_methods || catalog.connection_methods).map((m) => [m, roleLabel(m)])} />
            <Pick value={f.environment} onChange={(v) => setF({ ...f, environment: v, managed: '' })} testId="wizard-environment" options={[['sandbox', 'Sandbox'], ['production', 'Production']]} />
            <Input placeholder="Connection name (e.g. Production boarding)" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="wizard-label" />
            {(!provider?.environments) && <Input placeholder="API base URL" value={f.base_url} onChange={(e) => setF({ ...f, base_url: e.target.value })} data-testid="wizard-base-url" />}
          </>}
          {step === 2 && <>
            {managedOpts.length > 0 && (
              <Pick value={f.managed || '__none'} onChange={(v) => setF({ ...f, managed: v === '__none' ? '' : v })} placeholder="Use platform-managed credential" testId="wizard-managed"
                    options={[['__none', 'Use my own credentials'], ...managedOpts.map((m) => [m.env, m.label])]} />
            )}
            {reqs.length === 0 && <p className="text-xs text-muted-foreground" data-testid="wizard-no-credentials">This provider needs no API credentials.</p>}
            {reqs.map((r) => (
              <Input key={r.key} type={r.secret ? 'password' : 'text'} value={f.creds[r.key] || ''} onChange={(e) => setCred(r.key, e.target.value)}
                     placeholder={`${r.label}${r.required ? '' : ' (optional)'}${r.secret ? ' — stored encrypted, never shown again' : ''}`}
                     disabled={!!f.managed && !r.config} data-testid={`wizard-cred-${r.key}`} />
            ))}
          </>}
          {step === 3 && <>
            <div className="flex flex-wrap gap-1.5">{(provider?.capabilities || ['application_submission', 'status_lookup']).map((c) => <Badge key={c} variant="secondary">{roleLabel(c)}</Badge>)}</div>
            {provider?.default_status_map ? <p className="text-xs text-muted-foreground">Native status mapping included ({Object.keys(provider.default_status_map).length} statuses).</p> : (
              <textarea className="w-full h-28 rounded-md border bg-background p-2 text-xs font-mono" placeholder={'UW_REVIEW = under_review\nAPPROVED = approved'} value={f.status_map}
                        onChange={(e) => setF({ ...f, status_map: e.target.value })} data-testid="wizard-status-map" />
            )}
          </>}
        </div>
        <DialogFooter>
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="connect-back">Back</Button>}
          {step < 3 ? <Button disabled={step === 0 && !f.provider_slug} onClick={() => setStep(step + 1)} data-testid="connect-next">Next</Button>
            : <Button onClick={save} data-testid="connect-save">Save connection</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProcessorConnectionsPage() {
  const { token } = useAuthStore();
  const [conns, setConns] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [wizard, setWizard] = useState(false);
  const load = useCallback(() => {
    apiFetch(token, '/boarding/providers/connections').then(setConns).catch((e) => toast.error(e.message));
    apiFetch(token, '/boarding/providers/catalog').then(setCatalog).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-6" data-testid="processor-connections-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Plug className="w-6 h-6" /> Processor Connections</h1>
          <p className="text-sm text-muted-foreground">Provider-agnostic: each connection declares its capabilities, credentials and mappings.</p>
        </div>
        <Button onClick={() => setWizard(true)} disabled={!catalog} data-testid="connect-processor-btn"><Plus className="w-4 h-4 mr-1" /> Connect processor</Button>
      </div>
      {!conns.length && <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="connections-empty">No processor connections yet.</Card>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{conns.map((c) => <ConnectionCard key={c.id} c={c} token={token} reload={load} />)}</div>
      {wizard && catalog && <ConnectWizard catalog={catalog} token={token} onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}
    </div>
  );
}
